import { useSyncExternalStore } from 'react';
import { useRouter } from 'next/router';

const FALLBACK_OBE_URL = 'https://bookings.starrv.com/obe/direct/trip/create/motorhome/';

/**
 * Query param on the current page that opts booking links into the beta OBE origin
 * (`NEXT_PUBLIC_BETA_BOOK_NOW_URL`) instead of the default one. Accepts `1`, `true` or `yes`.
 */
const BETA_OBE_TOGGLE_PARAM = 'useBetaObe';
const BETA_OBE_TOGGLE_TRUTHY = new Set(['1', 'true', 'yes']);

export function shouldUseBetaObe(): boolean {
    if (typeof window === 'undefined' || !process.env.NEXT_PUBLIC_BETA_BOOK_NOW_URL) {
        return false;
    }
    const value = new URLSearchParams(window.location.search).get(BETA_OBE_TOGGLE_PARAM);
    return value !== null && BETA_OBE_TOGGLE_TRUTHY.has(value.toLowerCase());
}

export function getDefaultObeBaseUrl(): URL {
    return new URL(process.env.NEXT_PUBLIC_BOOK_NOW_URL || FALLBACK_OBE_URL);
}

export function getObeBaseUrl(): URL {
    const betaUrl = process.env.NEXT_PUBLIC_BETA_BOOK_NOW_URL;
    return shouldUseBetaObe() && betaUrl ? new URL(betaUrl) : getDefaultObeBaseUrl();
}

// The beta toggle only changes on a full navigation, so there is nothing to subscribe to.
const subscribe = () => () => {};

/**
 * getObeBaseUrl() reads the window-only `?useBetaObe` toggle, so resolving it during render would
 * mismatch SSR (default origin) vs client (beta origin). The href is read via useSyncExternalStore:
 * the server/hydration snapshot forces the default origin to match the SSR HTML, then React
 * re-renders with the beta origin once hydrated (JUCY-1540).
 */
export const useObeUrl = () => {
    const route = useRouter();
    const countryCodeSegment = route.locale ? `${route.locale}/` : '/';
    const path = `/obe/${countryCodeSegment}direct/trip/create/motorhome`;
    const href = useSyncExternalStore(
        subscribe,
        () => new URL(path, getObeBaseUrl()).href,
        () => new URL(path, getDefaultObeBaseUrl()).href
    );
    return new URL(href);
};
